import { useCallback, useEffect, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ReplyIcon from '@mui/icons-material/Reply';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';

import { supabase } from '../providers/supabase/supabase';

type StaleConversation = {
    id: number;
    name: string;
    email: string;
    my_last_reply_at: string;
    their_last_reply_at: string | null;
    days_since_my_reply: number;
    checkin_count: number;
    spotify_monthly_listeners: number | null;
};

type AwaitingMyReply = {
    id: number;
    name: string;
    email: string;
    their_last_reply_at: string;
    days_waiting: number;
    spotify_monthly_listeners: number | null;
};

type ConversationStats = {
    status: string;
    count: number;
};

export const ConversationDashboard = () => {
    const [staleConversations, setStaleConversations] = useState<StaleConversation[]>([]);
    const [awaitingMyReply, setAwaitingMyReply] = useState<AwaitingMyReply[]>([]);
    const [stats, setStats] = useState<ConversationStats[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sendingCheckin, setSendingCheckin] = useState<number | null>(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);

        const [staleRes, statsRes, awaitingRes] = await Promise.all([
            supabase.rpc('get_stale_conversations', { days_threshold: 7 }),
            supabase.rpc('conversation_stats'),
            supabase
                .from('regression_prospects')
                .select('id, name, email, their_last_reply_at, spotify_monthly_listeners')
                .eq('conversation_status', 'awaiting_my_reply')
                .eq('sequence_status', 'artist_replied')
                .not('their_last_reply_at', 'is', null)
                .order('their_last_reply_at', { ascending: true }),
        ]);

        if (staleRes.error) {
            setError(staleRes.error.message);
        } else {
            setStaleConversations(staleRes.data ?? []);
        }

        if (statsRes.error) {
            setError((prev) => prev ?? statsRes.error!.message);
        } else {
            setStats(statsRes.data ?? []);
        }

        if (awaitingRes.error) {
            setError((prev) => prev ?? awaitingRes.error!.message);
        } else {
            const withDays = (awaitingRes.data ?? []).map((item) => ({
                ...item,
                days_waiting: Math.floor(
                    (Date.now() - new Date(item.their_last_reply_at).getTime()) / (1000 * 60 * 60 * 24)
                ),
            }));
            setAwaitingMyReply(withDays);
        }

        setLoading(false);
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleMarkResolved = async (id: number) => {
        const { error: updateError } = await supabase
            .from('regression_prospects')
            .update({ conversation_status: 'cold' })
            .eq('id', id);

        if (updateError) {
            setError(updateError.message);
        } else {
            fetchData();
        }
    };

    const handleMarkReplied = async (id: number) => {
        const { error: updateError } = await supabase
            .from('regression_prospects')
            .update({
                conversation_status: 'awaiting_their_reply',
                my_last_reply_at: new Date().toISOString(),
            })
            .eq('id', id);

        if (updateError) {
            setError(updateError.message);
        } else {
            fetchData();
        }
    };

    const handleSendCheckin = async (prospect: StaleConversation) => {
        setSendingCheckin(prospect.id);

        try {
            const response = await fetch('/api/cron/send-checkins', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ prospectId: prospect.id }),
            });

            if (!response.ok) {
                throw new Error('Failed to send check-in');
            }

            fetchData();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setSendingCheckin(null);
        }
    };

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
        });
    };

    const formatListeners = (listeners: number | null) => {
        if (!listeners) return '-';
        if (listeners >= 1000000) return `${(listeners / 1000000).toFixed(1)}M`;
        if (listeners >= 1000) return `${(listeners / 1000).toFixed(0)}K`;
        return listeners.toString();
    };

    const getStatusColor = (status: string): 'default' | 'warning' | 'success' | 'error' | 'info' => {
        switch (status) {
            case 'awaiting_their_reply':
                return 'warning';
            case 'awaiting_my_reply':
                return 'info';
            case 'active':
                return 'success';
            case 'cold':
                return 'default';
            case 'converted':
                return 'success';
            default:
                return 'default';
        }
    };

    const getUrgencyColor = (days: number): string => {
        if (days >= 21) return '#d32f2f';
        if (days >= 14) return '#ed6c02';
        if (days >= 7) return '#2e7d32';
        return '#1976d2';
    };

    return (
        <Stack spacing={3} sx={{ py: 4 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box>
                    <Typography variant="h4" component="h1">
                        Conversations
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Track replies and follow-ups with artists
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
                    onClick={fetchData}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            {/* Stats cards */}
            <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ borderLeft: '4px solid #1976d2' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                You Owe Them
                            </Typography>
                            <Typography variant="h4" color="info.main">
                                {awaitingMyReply.length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ borderLeft: '4px solid #ed6c02' }}>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                Need Check-in
                            </Typography>
                            <Typography variant="h4" color="warning.main">
                                {staleConversations.length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                {stats.map((stat) => (
                    <Grid item xs={6} sm={4} md={2} key={stat.status}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                <Chip
                                    label={stat.status.replace(/_/g, ' ')}
                                    color={getStatusColor(stat.status)}
                                    size="small"
                                    sx={{ mb: 1 }}
                                />
                                <Typography variant="h4">{stat.count}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* YOU OWE THEM - Artists waiting for your reply */}
            <Card sx={{ borderTop: '3px solid #1976d2' }}>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <ReplyIcon color="info" />
                        <Typography variant="h6">You Owe Them</Typography>
                        <Chip label={awaitingMyReply.length} size="small" color="info" />
                    </Box>
                    {loading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress />
                        </Box>
                    ) : awaitingMyReply.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No pending replies. You're all caught up!
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Artist</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell align="right">Listeners</TableCell>
                                        <TableCell>They Replied</TableCell>
                                        <TableCell align="center">Waiting</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {awaitingMyReply.map((conv) => (
                                        <TableRow key={conv.id} sx={{ backgroundColor: 'rgba(25, 118, 210, 0.04)' }}>
                                            <TableCell>
                                                <Typography fontWeight="medium">{conv.name}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    component="a"
                                                    href={`mailto:${conv.email}`}
                                                    sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                                >
                                                    {conv.email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">{formatListeners(conv.spotify_monthly_listeners)}</TableCell>
                                            <TableCell>{formatDate(conv.their_last_reply_at)}</TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    icon={<ScheduleIcon />}
                                                    label={`${conv.days_waiting}d`}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: getUrgencyColor(conv.days_waiting),
                                                        color: 'white',
                                                        '& .MuiChip-icon': { color: 'white' },
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="I replied (move to waiting on them)">
                                                    <IconButton color="primary" size="small" onClick={() => handleMarkReplied(conv.id)}>
                                                        <CheckIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* WAITING ON THEM - Stale conversations needing check-in */}
            <Card sx={{ borderTop: '3px solid #ed6c02' }}>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <ScheduleIcon color="warning" />
                        <Typography variant="h6">Waiting on Them (7+ days)</Typography>
                        <Chip label={staleConversations.length} size="small" color="warning" />
                    </Box>
                    {loading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress />
                        </Box>
                    ) : staleConversations.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No stale conversations needing check-in.
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Artist</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell align="right">Listeners</TableCell>
                                        <TableCell>My Last Reply</TableCell>
                                        <TableCell align="center">Days Silent</TableCell>
                                        <TableCell align="center">Check-ins</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {staleConversations.map((conv) => (
                                        <TableRow key={conv.id}>
                                            <TableCell>
                                                <Typography fontWeight="medium">{conv.name}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {conv.email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">{formatListeners(conv.spotify_monthly_listeners)}</TableCell>
                                            <TableCell>{formatDate(conv.my_last_reply_at)}</TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    icon={<ScheduleIcon />}
                                                    label={`${conv.days_since_my_reply}d`}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: getUrgencyColor(conv.days_since_my_reply),
                                                        color: 'white',
                                                        '& .MuiChip-icon': { color: 'white' },
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip label={conv.checkin_count} size="small" variant={conv.checkin_count > 0 ? 'filled' : 'outlined'} />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Send AI check-in email">
                                                    <span>
                                                        <IconButton
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => handleSendCheckin(conv)}
                                                            disabled={sendingCheckin === conv.id || conv.checkin_count >= 3}
                                                        >
                                                            {sendingCheckin === conv.id ? <CircularProgress size={18} /> : <SendIcon />}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Mark as cold">
                                                    <IconButton color="default" size="small" onClick={() => handleMarkResolved(conv.id)}>
                                                        <CheckIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* Instructions */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Quick Sync
                    </Typography>
                    <Typography variant="body2" color="text.secondary" component="div">
                        Run daily to keep this dashboard accurate:
                        <Box component="code" sx={{ display: 'block', mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                            node scripts/sync-conversations.js
                        </Box>
                    </Typography>
                </CardContent>
            </Card>
        </Stack>
    );
};
